export interface Document {
  id: string;
  public_id: string;
  organization_id: string;
  entity_type: 'job' | 'rfq' | 'bid' | 'completion';
  entity_id: string;
  filename: string;
  original_filename: string;
  size_bytes: number;
  content_type: string;
  storage_key: string;
  checksum?: string;
  virus_scan_status: 'pending' | 'clean' | 'infected';
  uploaded_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export type DocumentKind = 'shipment' | 'proof_of_delivery' | 'warranty' | 'certification' | 'invoice' | 'other';

export interface DocumentUpload {
  document_id: string;
  kind: DocumentKind;
  title: string;
  description?: string;
}

export interface JobCompletion {
  id: string;
  public_id: string;
  job_id: string;
  organization_id: string;
  submitted_by_user_id: string;
  completion_notes?: string;
  actual_completion_date: string;
  delivery_date?: string;
  submitted_at: string;
  reviewed_at?: string;
  reviewed_by_user_id?: string;
  review_result?: 'accepted' | 'rejected';
  review_reason_code?: string;
  review_notes?: string;
  resubmission_count: number;
  max_resubmissions: number;
  auto_accept_at: string;
  created_at: string;
  updated_at: string;
  documents?: Document[];
}

export type DisputeStatus = 'open' | 'under_review' | 'resolved_for_client' | 'resolved_for_vendor';

export interface Dispute {
  id: string;
  public_id: string;
  job_id: string;
  organization_id: string;
  created_by_user_id: string;
  reason: string;
  dispute_type?: string;
  supporting_documents: any[];
  requested_resolution?: string;
  status: DisputeStatus;
  assigned_admin_id?: string;
  admin_notes?: string;
  resolution_notes?: string;
  resolved_by_user_id?: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
  // Joined data for display
  job?: {
    public_id: string;
    title: string;
    budget_cents: number;
    estimated_start_date?: string;
    due_date?: string;
  };
  client_organization?: {
    name: string;
  };
}

export interface DisputeDetails extends Dispute {
  client?: {
    first_name?: string;
    last_name?: string;
    organization_name?: string;
  };
  vendor?: {
    first_name?: string;
    last_name?: string;
    business_name?: string;
  };
}

export type ReviewReasonCode = 'late' | 'damaged' | 'not_as_specified' | 'incomplete' | 'quality_issues' | 'other';

export const REVIEW_REASON_CODES: { value: ReviewReasonCode; label: string }[] = [
  { value: 'late', label: 'Late Delivery' },
  { value: 'damaged', label: 'Damaged Items' },
  { value: 'not_as_specified', label: 'Not as Specified' },
  { value: 'incomplete', label: 'Incomplete Delivery' },
  { value: 'quality_issues', label: 'Quality Issues' },
  { value: 'other', label: 'Other' },
];

export const DOCUMENT_KINDS: { value: DocumentKind; label: string }[] = [
  { value: 'shipment', label: 'Shipment Documentation' },
  { value: 'proof_of_delivery', label: 'Proof of Delivery' },
  { value: 'warranty', label: 'Warranty Information' },
  { value: 'certification', label: 'Certification' },
  { value: 'invoice', label: 'Invoice' },
  { value: 'other', label: 'Other' },
];

export const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB
export const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
];